import { useCallback, useState } from 'react';
import { serializeFormData } from './serializeFormData';

type ActionCallback<T> = (data: T, nextData: T) => Promise<T> | T;

export type Action = (data: FormData | null) => Promise<void>;

type FormActionState<T> = [{ data: T; error: Error | null }, Action, boolean];

export const useFormState = <T extends object>(
  action: ActionCallback<T>,
  initialData: Partial<T> = {} as T,
): FormActionState<T> => {
  const [data, setData] = useState<T>(initialData as T);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const formAction = useCallback(
    async (nextFormData: FormData | null) => {
      if (nextFormData === null) {
        setData(initialData as T);
        setError(null);
        return;
      }

      const nextData = serializeFormData(nextFormData) as T;
      setIsPending(true);

      try {
        const result = await action(data, nextData);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsPending(false);
      }
    },
    [action, initialData, data],
  );

  return [
    {
      data,
      error,
    },
    formAction,
    isPending,
  ];
};
